<div class="container">
  @if (isUserUpdating()) {
    <p class="warning">Your account is currently being updated, please come back later.</p>
  }
  @if (errorMessage) {
    <p class="warning">{{ errorMessage }}</p>
  }
  @if (!user.course_code) {
    <p class="warning" role="status" id="no_course_warning" aria-label="No Assignment system available">
      You have arrived at Noteable without any <em>Course</em> details: the Assignments system will not be available to
      you.
    </p>
  }

  <!-- Notebook Selection + Help/Guides Panel -->
  <div class="container-selector-panels">
    @if (showStartButtons()) {
      <div id="notebook-selection">
        <div class="support-box notebook-select" id="notebook-selector-form">
          <form [formGroup]="form" aria-label="Notebook Server selector">
            <label
              ><h2>Please select a personal notebook server</h2>
              <select
                name="containerSelect"
                id="containerSelect"
                aria-label="Select notebook"
                formControlName="select"
                [attr.disabled]="isServerLive() || isUserUpdating() ? true : null">
                <!-- prettier-ignore -->
                @for (container of containers; track container.id) {
                  <option [value]="container.id">
                    {{ container.display_name }}
                  </option>
                }
              </select>
            </label>
            <div>
              <button
                class="launch-page-button"
                id="start-button"
                (click)="startServer()"
                [attr.disabled]="submit.getSubmitting() || isServerLive() || isUserUpdating() ? true : null"
                [innerHTML]="submit.getSubmitText()"
                type="submit"></button>
            </div>
          </form>
        </div>

        <!-- Help and Guides -->
        <section aria-labelledby="h3_guides_diag" class="support-box" id="help_guides">
          <h3 id="h3_guides_diag">
            <a href="/help_guides" aria-label="A collection of Help and Guides resources">Help and Guides</a>
          </h3>
          <ul>
            <li>Helpful resources for students, lecturers, school teachers and users new to notebooks</li>
            <li>Guides for assignments, collaboration, multiple markers in a course and more</li>
          </ul>
        </section>
      </div>
    } @else {
      <div id="running_notebook_options">
        <h2 id="running_notebook_buttons">
          @if (isServerLive()) {
            You have a running Notebook Server.
          } @else {
            Your Notebook Server is starting.
          }
        </h2>
        <p>
          @if (isServerLive()) {
            Would you like to reconnect to it, or shut it down?
          }
        </p>
        @if (canCancelStartup()) {
          <button
            type="button"
            class="launch-page-button"
            id="cancel-startup-button"
            title="Cancel"
            (click)="cancelStartup()"
            [attr.disabled]="shutdown.getSubmitting() ? true : null">
            Cancel
          </button>
        } @else {
          <button
            type="button"
            class="launch-page-button"
            (click)="reconnect()"
            [attr.disabled]="!isServerLive() || isUserUpdating() || isConnecting() ? true : null">
            Reconnect
          </button>
          <button
            type="button"
            class="launch-page-button"
            id="stop-button"
            title="Stop"
            (click)="stopServer()"
            [attr.disabled]="shutdown.getSubmitting() || !canStopServer() ? true : null"
            [innerHTML]="shutdown.getSubmitText()"></button>
        }
      </div>
    }
    @if (progressText) {
      <section role="status" [attr.aria-label]="progressText" id="progress_info">
        <pre>{{ progressText }}</pre>
      </section>
    }
  </div>

  <!-- Notebook Description - always shown for the currently selected notebook -->
  @if (selectedNotebook) {
    <div
      id="description_content_section"
      class="support-box notebook-description-boxes brief-description-box notebook-description-main-content"
      aria-live="polite"
      aria-atomic="true">
      <section
        class="notebook-description-summary"
        [attr.aria-label]="selectedNotebook.display_name + ' brief description'">
        <h3 id="notebook_desc_title">{{ selectedNotebook.display_name }}</h3>
        @if (selectedNotebook.restricted) {
          <h4 id="h4_{{ selectedNotebook.description.service }}_restricted">
            (Access granted to a restricted notebook)
          </h4>
        }
        <p>{{ selectedNotebook.description.description }}</p>
        <ul>
          @for (section of selectedNotebook.description.sections; track $index) {
            @if ((!section.libraries || section.libraries.length === 0) && section.prepend) {
              <li>
                <span [innerHTML]="section.prepend"></span>
              </li>
            }
          }
        </ul>
        <button
          type="button"
          class="launch-page-button"
          [attr.aria-label]="'Read more details about ' + selectedNotebook.display_name"
          (click)="openDetails()">
          Read more
        </button>
      </section>

      <aside class="notebook-description-aside" aria-label="Full list of all notebook descriptions">
        <p>
          To compare the similarities and differences, the
          <a class="notebook-descriptions-link" href="/notebook-descriptions">Notebook Descriptions</a>
          page contains the full list for all available notebook servers.
        </p>
      </aside>
    </div>
  }

  <!-- Modal Dialog -->
  @if (selectedNotebook) {
    <dialog
      class="support-box notebook-description-boxes modal-dialog"
      id="description_modal"
      aria-labelledby="modal_desc_title"
      closedby="none"
      close="">
      <div class="modal-body">
        <h3 id="modal_desc_title">{{ selectedNotebook.display_name }}</h3>
        @if (selectedNotebook.restricted) {
          <h4 id="h4_{{ selectedNotebook.description.service }}_restricted">
            (Access granted to a restricted notebook)
          </h4>
        }
        <p>{{ selectedNotebook.description.description }}</p>
        <ul>
          @for (section of selectedNotebook.description.sections; track $index) {
            <li>
              <span [innerHTML]="section | getListItemText"></span>
            </li>
          }
        </ul>
        <button
          type="button"
          id="close-description-button"
          data-testid="modal-close-button"
          class="launch-page-button"
          (click)="closeDetails()">
          Close
        </button>
      </div>
    </dialog>
  }
</div>
